import clsx from 'clsx';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { formatPhone } from '../../../shared/phone';
import { Row } from '../../../ui/v2';
import { Spinner } from '../../../ui/v2';
import { Callout, Reason } from '../../../ui/v2';
import { Input } from '../../../ui/v2';
import { StatusLabel, Tag } from '../../../ui/v2';
import { IconExternal, IconSearch } from '../../../ui/v2';
import {
  useGoogleActions,
  useGooglePlaceQuery,
  useStoreInfoQuery,
} from './data';
import {
  type GooglePlaceLink,
  busyStatus,
  displayWebsite,
  restaurantName,
} from './model';
import {
  Block,
  BlockSkeleton,
  QueryGate,
  SavedNote,
  SectionBody,
  SectionHead,
  useSavedAt,
} from './shared';

/**
 * Overview — read-only facts as a definition list (two columns ≥640px), plus
 * the platform-admin Google Place block. Nothing here is edited by the store;
 * the facts render as text instead of disabled inputs.
 */
const OverviewSection: React.FC = () => {
  const isPlatformAdmin = !!useAuth().user?.is_platform_admin;
  const info = useStoreInfoQuery('Store details could not be loaded.');

  return (
    <SectionBody measure="overview">
      <SectionHead title="Overview" />
      <div className="mt-6">
        <QueryGate query={info} skeleton={<BlockSkeleton rows={6} />}>
          {(store) => {
            const status = busyStatus(store.busy_mode_status);
            const address = store.store_address || store.address;
            const phone = formatPhone(store.location_secondary_phone_number);
            const yesNo = (v: boolean) => (v ? 'Yes' : 'No');
            return (
              <dl className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                <div>
                  <Row label="Store name">{restaurantName(store)}</Row>
                  <Row label="Address" empty={!address}>
                    {address}
                  </Row>
                  <Row label="Website" empty={!store.website}>
                    {store.website && (
                      <a
                        href={store.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-v2-accent hover:underline"
                      >
                        {displayWebsite(store.website)}
                        <IconExternal size={12} />
                      </a>
                    )}
                  </Row>
                  <Row label="Phone" empty={!phone}>
                    <span className="v2-num">{phone}</span>
                  </Row>
                </div>
                <div>
                  <Row label="Status" empty={!status}>
                    {status && (
                      <StatusLabel tone={status.tone}>
                        {status.label}
                        {status.label === 'Busy' &&
                        store.busy_mode_delay_minutes ? (
                          <span className="v2-num font-normal text-v2-fg-3">
                            · quoting +{store.busy_mode_delay_minutes} min
                          </span>
                        ) : null}
                      </StatusLabel>
                    )}
                  </Row>
                  <Row label="Takes reservations">
                    {yesNo(store.takes_reservations)}
                  </Row>
                  <Row label="Outdoor seating">
                    {yesNo(store.outdoor_seating)}
                  </Row>
                  <Row label="Parking" empty={!store.parking_info}>
                    {store.parking_info}
                  </Row>
                </div>
              </dl>
            );
          }}
        </QueryGate>
      </div>
      {isPlatformAdmin && <GooglePlaceBlock />}
    </SectionBody>
  );
};

// ---------------------------------------------------------------------------
// Google Place (platform admin only — hidden, not disabled, for everyone else)
// ---------------------------------------------------------------------------

const MIN_QUERY = 2;

const GooglePlaceBlock: React.FC = () => {
  const place = useGooglePlaceQuery(true);
  const googleActions = useGoogleActions();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GooglePlaceLink[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const [linking, setLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, markSaved] = useSavedAt();
  const [savedText, setSavedText] = useState('Saved.');
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      requestId.current += 1;
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    const id = ++requestId.current;
    setSearching(true);
    setOpen(true);
    const t = window.setTimeout(async () => {
      try {
        const found = await googleActions.search(trimmed);
        if (requestId.current !== id) return;
        setResults(found);
        setActive(0);
        setError(null);
      } catch (e) {
        if (requestId.current !== id) return;
        setResults([]);
        setError(
          e instanceof Error ? e.message : 'Google place search failed.',
        );
      } finally {
        if (requestId.current === id) setSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [query, googleActions]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const link = async (p: GooglePlaceLink) => {
    setLinking(p.place_id);
    setError(null);
    try {
      const same = p.place_id === place.data?.place_id;
      await googleActions.link(p.place_id);
      requestId.current += 1;
      setQuery('');
      setOpen(false);
      setResults([]);
      setSavedText(
        same
          ? 'Hours refreshed from this Google place.'
          : 'Linked this Google place and updated the weekly hours.',
      );
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not update the Google place.',
      );
    } finally {
      setLinking(null);
    }
  };

  return (
    <Block
      quiet
      className="!mt-12"
      title="Google place"
      description="The Google listing this store's automatic weekly hours come from. Linking a different place replaces the schedule when hours are automatic."
      control={<Tag>Platform admin</Tag>}
    >
      <QueryGate query={place} skeleton={<BlockSkeleton rows={2} />}>
        {(linked) => (
          <>
            <dl className="max-w-[560px]">
              <Row label="Linked listing" empty={!linked}>
                {linked ? (
                  <span className="flex flex-col">
                    <span className="font-medium">{linked.name}</span>
                    <span className="text-v2-12 text-v2-fg-3">
                      {linked.formatted_address}
                    </span>
                  </span>
                ) : (
                  'Not linked yet'
                )}
              </Row>
              {linked?.maps_url && (
                <Row label="Maps">
                  <a
                    href={linked.maps_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-v2-accent hover:underline"
                  >
                    Open in Google Maps
                    <IconExternal size={12} />
                  </a>
                </Row>
              )}
            </dl>
            <div ref={rootRef} className="relative mt-4 max-w-[560px]">
              <label
                htmlFor={`${listboxId}-input`}
                className="mb-1.5 block text-v2-12 font-medium text-v2-fg-2"
              >
                {linked ? 'Link a different place' : 'Find the Google place'}
              </label>
              <Input
                id={`${listboxId}-input`}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={
                  open && results[active]
                    ? `${listboxId}-${results[active].place_id}`
                    : undefined
                }
                autoComplete="off"
                placeholder="Search by name or address"
                leading={linking ? <Spinner /> : <IconSearch size={14} />}
                value={query}
                disabled={linking !== null}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() =>
                  query.trim().length >= MIN_QUERY &&
                  results.length > 0 &&
                  setOpen(true)
                }
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setOpen(true);
                    setActive((i) =>
                      results.length ? (i + 1) % results.length : 0,
                    );
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActive((i) =>
                      results.length
                        ? (i - 1 + results.length) % results.length
                        : 0,
                    );
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                  } else if (e.key === 'Enter' && open && results[active]) {
                    e.preventDefault();
                    void link(results[active]);
                  }
                }}
              />
              {open && (
                <ul
                  id={listboxId}
                  role="listbox"
                  className="v2-fade-in absolute z-50 mt-1 w-full rounded-v2-lg border border-v2-line bg-v2-surface p-1 shadow-v2-float"
                >
                  {searching && results.length === 0 && (
                    <li className="px-2 py-2 text-v2-12 text-v2-fg-3">
                      Searching…
                    </li>
                  )}
                  {!searching && results.length === 0 && (
                    <li className="px-2 py-2 text-v2-12 text-v2-fg-3">
                      No Google places matched that search.
                    </li>
                  )}
                  {results.map((p, i) => {
                    const isCurrent = p.place_id === linked?.place_id;
                    return (
                      <li key={p.place_id} role="presentation">
                        <button
                          id={`${listboxId}-${p.place_id}`}
                          type="button"
                          role="option"
                          aria-selected={i === active}
                          disabled={linking !== null}
                          onMouseEnter={() => setActive(i)}
                          onClick={() => void link(p)}
                          className={clsx(
                            'flex w-full flex-col rounded-v2 px-2 py-1.5 text-left transition-colors',
                            i === active
                              ? 'bg-v2-sunken'
                              : 'hover:bg-v2-sunken/60',
                          )}
                        >
                          <span className="flex items-center gap-2 text-v2-13 font-medium text-v2-fg">
                            {p.name}
                            {isCurrent && <Tag>Current</Tag>}
                          </span>
                          <span className="text-v2-12 text-v2-fg-3">
                            {p.formatted_address}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="mt-1.5 flex items-center gap-3">
                <Reason>
                  {linking
                    ? 'Updating hours…'
                    : 'Choosing a result links it immediately.'}
                </Reason>
                <SavedNote when={savedAt}>{savedText}</SavedNote>
              </div>
            </div>
            {error && (
              <Callout tone="danger" className="mt-3 max-w-[560px]">
                {error}
              </Callout>
            )}
          </>
        )}
      </QueryGate>
    </Block>
  );
};

export default OverviewSection;
